/**
 * NumberLineSVG — Phase 1, Component 3 of the TeachBud Representation Catalog
 *
 * Renders a mathematically accurate number line per §5.1 Counting / Fractions:
 *   - K–1: 0–10 or 0–20, large integers, hop arcs for addition/subtraction
 *   - 2–3: 0–100 or custom range, interval tick marks
 *   - 4–5: Fraction number line (halves, thirds, fourths, eighths)
 *   - 6–8: Integer line spanning negatives
 *   - Student mode: tick marks shown but number labels hidden (student writes them)
 */

import React from 'react';

export interface NumberLineProps {
  /** Start value (default 0). Use negative for integer number line. */
  start?: number;
  /** End value (default 10). */
  end?: number;
  /**
   * Interval between labeled ticks. Default auto-derived from range.
   * For fraction mode pass a string like "1/4", "1/2", "1/3".
   */
  interval?: number;
  /** Fraction denominator — when set, renders a fraction number line (0 to 1 or 0 to 2). */
  fractionDenom?: number;
  /** Values to mark with hop arcs (addition/subtraction jumps). */
  hops?: number[];
  /** When true, number labels are hidden — student fills them in. */
  studentMode?: boolean;
  /** Override pixel width. */
  width?: number;
  /** Grade (0=K). Adjusts font sizes and tick heights. */
  grade?: number;
  className?: string;
}

// ── Layout ────────────────────────────────────────────────────────────────────
const VB_W = 560;
const PAD_X = 30;
const LINE_Y = 70;
const VB_H = 120;

// Colors
const LINE_COLOR    = '#374151';  // gray-700
const TICK_COLOR    = '#6B7280';  // gray-500
const LABEL_COLOR   = '#1E3A5F';  // dark navy
const HOP_COLOR     = '#2563EB';  // TeachBud blue
const NEG_TINT      = '#FEE2E2';  // red-100
const POS_TINT      = '#DBEAFE';  // blue-100
const FRAC_SHADE    = '#3B82F6';  // blue-500

// ── Helper: fraction to decimal ───────────────────────────────────────────────
function fracToDecimal(n: number, d: number) { return n / d; }

function formatLabel(v: number, fractionDenom?: number): string {
  if (!fractionDenom) return String(v);
  // For fraction number line: 0, 1/d, 2/d, …, 1, (d+1)/d …
  if (v === 0) return '0';
  if (v === 1) return '1';
  if (v === 2) return '2';
  // v is already a fraction expressed as multiples of 1/denom
  const denom = fractionDenom;
  const numer = Math.round(v * denom);
  if (numer % denom === 0) return String(numer / denom);
  return `${numer}/${denom}`;
}

export function NumberLineSVG({
  start = 0,
  end = 10,
  interval,
  fractionDenom,
  hops,
  studentMode = false,
  width,
  grade = 1,
  className = '',
}: NumberLineProps) {
  const isNegative = start < 0;
  const isFraction = fractionDenom != null && fractionDenom > 1;

  // ── Compute ticks ──────────────────────────────────────────────────────────
  let ticks: number[] = [];
  if (isFraction) {
    // Generate ticks at every 1/denom from start to end
    const totalUnits = (end - start) * fractionDenom!;
    for (let i = 0; i <= totalUnits; i++) {
      ticks.push(start + i / fractionDenom!);
    }
  } else {
    const step = interval ?? Math.max(1, Math.round((end - start) / 10));
    for (let v = start; v <= end; v += step) {
      ticks.push(Math.round(v * 1000) / 1000);
    }
    if (ticks[ticks.length - 1] !== end) ticks.push(end);
  }

  // ── x position mapping ─────────────────────────────────────────────────────
  function xOf(v: number) {
    return PAD_X + ((v - start) / (end - start)) * (VB_W - 2 * PAD_X);
  }

  const TICK_MAJOR_H = grade <= 2 ? 14 : 10;
  const TICK_MINOR_H = 6;
  const LABEL_FS = grade <= 1 ? 18 : grade <= 3 ? 14 : 12;

  // Determine which ticks are "major" (carry visible labels)
  function isMajor(v: number, idx: number): boolean {
    if (isFraction) {
      // major = integers
      return Math.abs(v - Math.round(v)) < 0.001;
    }
    return true; // all ticks labeled when not fraction
  }

  // ── Arrowhead at end ───────────────────────────────────────────────────────
  const arrowX = VB_W - PAD_X + 14;

  return (
    <svg
      viewBox={`0 0 ${VB_W} ${VB_H}`}
      width={width ?? VB_W}
      height={width ? Math.round((VB_H / VB_W) * width) : VB_H}
      className={className}
      role="img"
      aria-label={studentMode ? 'Number line — student fills in' : `Number line from ${start} to ${end}`}
    >
      <title>Number line {start} to {end}</title>

      {/* Negative region tint */}
      {isNegative && start < 0 && (
        <rect
          x={PAD_X} y={LINE_Y - 18}
          width={xOf(0) - PAD_X}
          height={36}
          fill={NEG_TINT} rx={4}
          opacity={0.5}
        />
      )}
      {/* Positive region tint for integer number line */}
      {isNegative && (
        <rect
          x={xOf(0)} y={LINE_Y - 18}
          width={VB_W - PAD_X - xOf(0)}
          height={36}
          fill={POS_TINT} rx={4}
          opacity={0.5}
        />
      )}

      {/* Main line */}
      <line
        x1={PAD_X} y1={LINE_Y}
        x2={arrowX - 6} y2={LINE_Y}
        stroke={LINE_COLOR} strokeWidth={isNegative || grade <= 2 ? 3 : 2.5} strokeLinecap="round"
      />
      {/* Arrowhead */}
      <polygon
        points={`${arrowX - 6},${LINE_Y - 5} ${arrowX},${LINE_Y} ${arrowX - 6},${LINE_Y + 5}`}
        fill={LINE_COLOR}
      />

      {/* Left cap */}
      <line x1={PAD_X} y1={LINE_Y - 8} x2={PAD_X} y2={LINE_Y + 8} stroke={LINE_COLOR} strokeWidth={2} />

      {/* Tick marks and labels */}
      {ticks.map((v, idx) => {
        const x = xOf(v);
        const major = isMajor(v, idx);
        const tickH = major ? TICK_MAJOR_H : TICK_MINOR_H;
        const isZero = Math.abs(v) < 0.001;

        return (
          <g key={idx}>
            <line
              x1={x} y1={LINE_Y - tickH}
              x2={x} y2={LINE_Y + tickH}
              stroke={isZero && isNegative ? LINE_COLOR : TICK_COLOR}
              strokeWidth={isZero && isNegative ? 3 : major ? 2 : 1}
            />
            {major && !studentMode && (
              <text
                x={x} y={LINE_Y + tickH + LABEL_FS + 2}
                textAnchor="middle"
                fontSize={LABEL_FS}
                fontFamily={grade <= 2 ? 'Nunito, sans-serif' : 'Inter, sans-serif'}
                fontWeight={isZero ? 700 : 400}
                fill={LABEL_COLOR}
              >
                {formatLabel(v, fractionDenom)}
              </text>
            )}
            {major && studentMode && (
              /* blank underline for student to write */
              <line
                x1={x - 9} y1={LINE_Y + tickH + LABEL_FS + 4}
                x2={x + 9} y2={LINE_Y + tickH + LABEL_FS + 4}
                stroke="#D1D5DB" strokeWidth={1}
              />
            )}
            {/* Fraction shading below major tick marks */}
            {isFraction && major && v > start && v < end && (
              <line x1={x} y1={LINE_Y - 6} x2={x} y2={LINE_Y + 6} stroke={FRAC_SHADE} strokeWidth={2.5} />
            )}
          </g>
        );
      })}

      {/* Hop arcs */}
      {hops && hops.length >= 2 && (
        hops.slice(0, -1).map((from, i) => {
          const to = hops[i + 1];
          const x1 = xOf(from);
          const x2 = xOf(to);
          const mx = (x1 + x2) / 2;
          const arcH = 22;
          return (
            <path
              key={i}
              d={`M ${x1},${LINE_Y - 2} Q ${mx},${LINE_Y - arcH} ${x2},${LINE_Y - 2}`}
              fill="none"
              stroke={HOP_COLOR}
              strokeWidth={2}
              strokeDasharray="5 3"
            />
          );
        })
      )}
    </svg>
  );
}

// ── Hint parser ───────────────────────────────────────────────────────────────
export function parseNumberLineHint(hint: string): NumberLineProps {
  const h = hint.toLowerCase();
  // Fraction number line
  const fracMatch = h.match(/(\d+)\/(\d+)/);
  if (fracMatch || /fraction.*number.?line|number.?line.*fraction/i.test(h)) {
    const denom = fracMatch ? parseInt(fracMatch[2]) : 4;
    return { start: 0, end: 2, fractionDenom: denom, studentMode: true };
  }
  // Integer number line
  if (/integer|negative/.test(h)) {
    return { start: -10, end: 10, studentMode: true };
  }
  // Range hint: "number line 0 to 20"
  const rangeMatch = h.match(/(\d+)\s+to\s+(\d+)/);
  if (rangeMatch) {
    return { start: parseInt(rangeMatch[1]), end: parseInt(rangeMatch[2]), studentMode: true };
  }
  // Default student number line 0–10
  return { start: 0, end: 10, studentMode: true };
}
